import * as THREE from 'three';
import type { FloraCatalog } from '@/flora/FloraCatalog';
import type { FloraDefinition } from '@/flora/FloraDefinition';
import type { FloraInstance, Vec3 } from '@/flora/FloraInstance';
import { createFloraInstance } from '@/flora/FloraInstance';
import { FloraFactory } from '@/flora/FloraFactory';
import { computeGrowthState } from '@/flora/FloraGrowthModel';
import type { FloraWorld } from '@/flora/FloraWorld';
import { blockingObject, type SiteObject } from '@/site/SiteObject';
import type { Terrain } from '@/terrain/Terrain';
import { TerrainRaycaster } from '@/terrain/TerrainRaycaster';
import { findSurfaceLayer, isPlantableSurface, type SurfaceLayerId } from '@/terrain/TerrainSurface';
import {
  clampUnit,
  holdDabCount,
  sampleStrokeSegment,
  type BrushPoint,
} from '@/terrain/TerrainBrush';
import {
  clampPlantBrushRadius,
  dartsPerDab,
  DEFAULT_PLANT_BRUSH,
  defaultPlantMode,
  MAX_PLANTS_PER_STROKE,
  otherPlantMode,
  PLANT_DAB_SPACING,
  PLANT_HOLD_DABS_PER_SECOND,
  plantModeLabel,
  ScatterField,
  scatterDarts,
  scatterSpacingMeters,
  scatterVariation,
  suggestedBrushRadius,
  type PlantBrushShape,
  type PlantToolMode,
} from './PlantBrush';
import { TerrainBrushCursor } from './TerrainBrushCursor';

/** Samples terrain surface height (meters) at a world (x, z). */
export type HeightSampler = (x: number, z: number) => number;

/** Footprint radius (meters) of a plant at a given maturity — half its current spread. */
export function placementFootprintRadius(def: FloraDefinition, maturity: number): number {
  const state = computeGrowthState(def.growth, maturity);
  return state.spreadMeters / 2;
}

/** Footprint radius (meters) the plant will occupy at maturity — used for the canopy overlay. */
export function matureFootprintRadius(def: FloraDefinition): number {
  return Math.max(def.placement.footprintRadiusMeters, def.growth.matureSpreadMeters.typical / 2);
}

/** Return a copy of `position` snapped down onto the terrain surface. */
export function snapToTerrain(position: Vec3, sampleHeight: HeightSampler): Vec3 {
  return { x: position.x, y: sampleHeight(position.x, position.z), z: position.z };
}

/** Estimate the local ground slope (degrees) around a point by sampling four neighbors. */
export function estimateSlopeDegrees(
  position: Vec3,
  sampleHeight: HeightSampler,
  sampleRadius = 0.5,
): number {
  const hxp = sampleHeight(position.x + sampleRadius, position.z);
  const hxn = sampleHeight(position.x - sampleRadius, position.z);
  const hzp = sampleHeight(position.x, position.z + sampleRadius);
  const hzn = sampleHeight(position.x, position.z - sampleRadius);
  const dx = (hxp - hxn) / (2 * sampleRadius);
  const dz = (hzp - hzn) / (2 * sampleRadius);
  const gradient = Math.hypot(dx, dz);
  return THREE.MathUtils.radToDeg(Math.atan(gradient));
}

export type PlacementRejection = 'slope-too-steep' | 'blocked-by-structure' | 'unplantable-surface';

export interface PlacementValidity {
  valid: boolean;
  slopeDegrees: number;
  reason?: PlacementRejection;
  /** Name of the building, paving, or ground material in the way. */
  blockedBy?: string;
}

export interface PlacementContext {
  /** Buildings and paving to keep clear of. Absent on a site with none. */
  siteObjects?: readonly SiteObject[];
  /**
   * The painted ground material at a point. Supplied once the terrain paint tool exists; a site with
   * nothing painted answers `grass` everywhere, so the rule costs nothing until it matters.
   */
  surfaceAt?: (x: number, z: number) => SurfaceLayerId;
}

/**
 * Decide whether a plant may be placed at a point (spec §3.5/§6.6).
 *
 * Three independent reasons to refuse: the ground is too steep for the species, something is already
 * built there, or the ground has been painted as something nothing grows in. The footprint test uses
 * the plant's own footprint radius as the margin, so a mature maple is refused before its trunk
 * reaches the wall rather than after its canopy has grown through it.
 *
 * The painted-surface rule is checked at the plant's own point rather than across its footprint,
 * because a shrub whose canopy overhangs a driveway is normal planting — a shrub *in* the driveway
 * is not.
 */
export function evaluatePlacement(
  def: FloraDefinition,
  position: Vec3,
  sampleHeight: HeightSampler,
  context: PlacementContext = {},
): PlacementValidity {
  const slopeDegrees = estimateSlopeDegrees(position, sampleHeight);

  const surface = context.surfaceAt?.(position.x, position.z);
  if (surface && !isPlantableSurface(surface)) {
    return {
      valid: false,
      slopeDegrees,
      reason: 'unplantable-surface',
      blockedBy: findSurfaceLayer(surface)?.name ?? surface,
    };
  }

  const blocker = context.siteObjects
    ? blockingObject(context.siteObjects, position.x, position.z, def.placement.footprintRadiusMeters * 0.35)
    : undefined;
  if (blocker) {
    return {
      valid: false,
      slopeDegrees,
      reason: 'blocked-by-structure',
      blockedBy: blocker.name,
    };
  }

  if (slopeDegrees > def.placement.maxSlopeDegrees) {
    return { valid: false, slopeDegrees, reason: 'slope-too-steep' };
  }
  return { valid: true, slopeDegrees };
}

/** Why the ghost is red, in words the status readout can show. */
export function describeRejection(validity: PlacementValidity | null): string {
  if (!validity || validity.valid) return '';
  if (validity.reason === 'blocked-by-structure') {
    return ` · blocked by ${validity.blockedBy ?? 'a structure'}`;
  }
  if (validity.reason === 'unplantable-surface') {
    return ` · nothing grows in ${(validity.blockedBy ?? 'that surface').toLowerCase()}`;
  }
  return ' · too steep';
}

export interface PlacementToolDeps {
  domElement: HTMLElement;
  getCamera: () => THREE.Camera;
  terrain: () => Terrain | null;
  world: FloraWorld;
  catalog: FloraCatalog;
  /** Called to commit a placement (wrapped in a command by Milestone 4). */
  onPlace: (instance: FloraInstance) => void;
  /**
   * Put a scattered plant into the world *now*, mid-stroke, without recording anything.
   *
   * A brush stroke has to be visible while it is being drawn — you are aiming at the edge of a bed
   * you can see filling in — but it is one act and must undo as one. So the two halves are separate
   * callbacks: this one draws, {@link onPaintCommit} remembers. Absent, the brush cannot paint and
   * the tool stays in spot mode.
   */
  onPaintPlace?: (instance: FloraInstance) => void;
  /** Record a finished stroke as a single undoable step, without re-applying it. */
  onPaintCommit?: (instances: readonly FloraInstance[]) => void;
  /** Buildings and paving that refuse planting. Optional — a site may have none. */
  siteObjects?: () => readonly SiteObject[];
  /** Called when the tool's observable state changes, so the HUD can re-read the mode. */
  onChange?: () => void;
  /**
   * A short line to show at the pointer for a few seconds — which mode is armed, what a stroke
   * planted. The tool says *what*; the HUD decides where it appears and when it fades.
   */
  onNotice?: (text: string) => void;
  /** Optional DOM element for the plant name/dimensions readout. */
  readout?: HTMLElement;
}

/**
 * The planting tool (spec §3.5), in two modes: place one, or paint a drift.
 *
 * ## Spot and paint are one tool, not two
 *
 * They share a plant, a ghost, a validity test and a readout; the only thing that differs is what
 * happens between pressing and releasing the mouse. Splitting them into two toolbar buttons would
 * have made the user choose a tool before choosing a plant, which is backwards — the plant is what
 * they came for, and it already implies the answer (see {@link defaultPlantMode}). So the mode arms
 * itself from the species and the toolbar keeps one Plant button.
 *
 * ## `Ctrl`: hold to borrow, tap to keep
 *
 * The mode is right almost always and wrong occasionally, and the two kinds of wrong want different
 * remedies. "One hosta at the corner of this drift" is a single action — hold `Ctrl`, click, let go.
 * "This whole bed of daylilies goes in as specimens" is a decision that holds for ten minutes —
 * tap `Ctrl` and it stays. Offering only the held form would make the second case a cramp; offering
 * only the toggle would make the first one two extra keystrokes and a mode to remember to leave.
 *
 * They coexist without ambiguity because a tap is a press *with nothing in between*: press `Ctrl`,
 * do anything at all — click, wheel, another key — and the release is just a release. `Ctrl`+`Z` is
 * therefore not a tap, and neither is `Ctrl`+wheel.
 *
 * ## Input while painting
 *
 * | Input | Effect |
 * | --- | --- |
 * | Left-drag | Scatter plants along the stroke |
 * | Hold `Ctrl` | Borrow the other mode until released |
 * | Tap `Ctrl` | Keep the other mode |
 * | `Ctrl` + wheel | Brush size |
 * | `Shift` + wheel | How tightly the drift packs |
 * | `[` / `]` | Previous / next plant, unchanged |
 * | `Esc` | Put the tool down |
 *
 * `Ctrl`+wheel matches the ground brushes exactly, which is worth more than avoiding the collision
 * with the mode key: spinning the wheel with `Ctrl` down puts `Ctrl` into size duty for as long as
 * it is held, so the pointer never flickers into the other mode while a brush is being sized.
 *
 * Strokes advance on the frame clock rather than on pointer events — see `TerrainBrush` for why —
 * so {@link tick} has to be called every frame for the brush to work.
 */
export class PlacementTool {
  private readonly raycaster = new TerrainRaycaster();
  private readonly factory = new FloraFactory();
  private readonly root = new THREE.Group();
  private readonly ghostRoot = new THREE.Group();
  /** The draped ring that shows the brush's reach. The same one the ground tools wear. */
  private readonly brushCursor = new TerrainBrushCursor();
  private ghostBody: THREE.Object3D | null = null;
  private footprintRing: THREE.Line | null = null;
  private canopyRing: THREE.Line | null = null;

  private active = false;
  private activeDefinitionId: string | null = null;
  private lastPoint: THREE.Vector3 | null = null;
  private lastValidity: PlacementValidity | null = null;
  private previewMaturity = 0.5;
  private nextVisualSeed = 1;

  /** The mode that stays. {@link effectiveMode} is this, with any borrowed one applied over it. */
  private mode: PlantToolMode = 'spot';
  /** True while `Ctrl` is borrowing the other mode. */
  private borrowing = false;
  /** True while the held `Ctrl` could still turn out to be a tap. */
  private tapPending = false;
  private readonly brush: PlantBrushShape = { ...DEFAULT_PLANT_BRUSH };

  /* Stroke state. Everything here is null/zero between strokes. */
  private pressed = false;
  private pointerId: number | null = null;
  private strokeField: ScatterField | null = null;
  private strokeSpacing = 0;
  private strokePlanted: FloraInstance[] = [];
  private lastDabPoint: BrushPoint | null = null;
  private carryMeters = 0;
  private holdSeconds = 0;

  private readonly local = new THREE.Vector2();

  private readonly onPointerMove = (e: PointerEvent): void => this.handlePointerMove(e);
  private readonly onPointerDown = (e: PointerEvent): void => this.handlePointerDown(e);
  private readonly onPointerUp = (e: PointerEvent): void => this.handlePointerUp(e);
  private readonly onKeyDown = (e: KeyboardEvent): void => this.handleKeyDown(e);
  private readonly onKeyUp = (e: KeyboardEvent): void => this.handleKeyUp(e);
  private readonly onWheel = (e: WheelEvent): void => this.handleWheel(e);
  // A window that loses focus with Ctrl down never delivers the keyup, which would leave the tool
  // borrowing a mode the user is no longer asking for.
  private readonly onBlur = (): void => this.releaseBorrow(false);

  constructor(private readonly deps: PlacementToolDeps) {
    this.root.name = 'placement-overlay';
    this.ghostRoot.name = 'placement-ghost';
    this.ghostRoot.visible = false;
    // Siblings, not parent and child: the ghost is moved to the pointer, while the brush ring lays
    // itself out in world coordinates and would be moved twice inside a translated group.
    this.root.add(this.ghostRoot, this.brushCursor.object);
  }

  /** The ghost preview and brush ring — the caller adds this to the scene overlay root. */
  get overlayObject(): THREE.Object3D {
    return this.root;
  }

  get isActive(): boolean {
    return this.active;
  }

  /** The mode that persists between strokes — what the toolbar and the palette report. */
  get plantMode(): PlantToolMode {
    return this.mode;
  }

  /** The mode the next press will actually use, `Ctrl` included. What the pointer draws. */
  get effectiveMode(): PlantToolMode {
    // Painting needs somewhere to put the plants; a host that wired no paint callbacks gets the
    // tool it asked for rather than a brush that silently drops every stroke.
    if (!this.deps.onPaintPlace || !this.deps.onPaintCommit) return 'spot';
    return this.borrowing ? otherPlantMode(this.mode) : this.mode;
  }

  /** The brush's current reach and packing. Read by the HUD for its readout. */
  get brushShape(): Readonly<PlantBrushShape> {
    return this.brush;
  }

  /** Centre-to-centre spacing the brush is holding for the armed plant, in metres. */
  get spacingMeters(): number {
    const def = this.currentDefinition();
    return def ? scatterSpacingMeters(def, this.brush.density) : 0;
  }

  /**
   * Begin placing the given plant, arming the mode the species asks for.
   *
   * The mode is re-derived on every arm rather than remembered across plants, because the answer
   * belongs to the plant: picking pachysandra after a maple should give a brush, and picking the
   * maple back should give the specimen tool, without either choice being a thing to undo.
   */
  start(definitionId: string): void {
    const first = !this.active || this.activeDefinitionId !== definitionId;
    this.activeDefinitionId = definitionId;
    this.active = true;
    if (first) this.armForDefinition();
    this.rebuildGhost();
    this.attachListeners();
    this.applyMode();
    this.updateReadout();
    if (first) this.announceMode();
    this.deps.onChange?.();
  }

  /** Cancel placement mode (Esc). */
  cancel(): void {
    this.commitStroke();
    this.active = false;
    this.ghostRoot.visible = false;
    this.brushCursor.setVisible(false);
    this.borrowing = false;
    this.tapPending = false;
    this.detachListeners();
    this.clearReadout();
    this.deps.onChange?.();
  }

  /** Choose the mode that stays. Announces itself, since nothing else on screen would. */
  setPlantMode(mode: PlantToolMode): void {
    if (this.mode === mode) return;
    this.mode = mode;
    this.applyMode();
    this.updateReadout();
    this.announceMode();
    this.deps.onChange?.();
  }

  togglePlantMode(): void {
    this.setPlantMode(otherPlantMode(this.mode));
  }

  setBrushRadius(meters: number): void {
    this.brush.radiusMeters = clampPlantBrushRadius(meters);
    this.refreshBrushCursor();
    this.updateReadout();
    this.deps.onChange?.();
  }

  setBrushDensity(density: number): void {
    this.brush.density = clampUnit(density, DEFAULT_PLANT_BRUSH.density);
    this.updateReadout();
    this.deps.onChange?.();
  }

  dispose(): void {
    this.cancel();
    this.disposeGhost();
    this.brushCursor.dispose();
  }

  private attachListeners(): void {
    // Re-arming while already active would otherwise stack a second copy of every listener.
    this.detachListeners();
    this.deps.domElement.addEventListener('pointermove', this.onPointerMove);
    this.deps.domElement.addEventListener('pointerdown', this.onPointerDown);
    this.deps.domElement.addEventListener('pointerup', this.onPointerUp);
    this.deps.domElement.addEventListener('pointercancel', this.onPointerUp);
    window.addEventListener('keydown', this.onKeyDown);
    window.addEventListener('keyup', this.onKeyUp);
    window.addEventListener('blur', this.onBlur);
    // Capture phase on window, ahead of the camera's own listener on the canvas, so a modified
    // wheel sizes the brush instead of zooming the site. See `TerrainEditor` for the same trick.
    window.addEventListener('wheel', this.onWheel, { capture: true, passive: false });
  }

  private detachListeners(): void {
    this.deps.domElement.removeEventListener('pointermove', this.onPointerMove);
    this.deps.domElement.removeEventListener('pointerdown', this.onPointerDown);
    this.deps.domElement.removeEventListener('pointerup', this.onPointerUp);
    this.deps.domElement.removeEventListener('pointercancel', this.onPointerUp);
    window.removeEventListener('keydown', this.onKeyDown);
    window.removeEventListener('keyup', this.onKeyUp);
    window.removeEventListener('blur', this.onBlur);
    window.removeEventListener('wheel', this.onWheel, { capture: true });
  }

  private currentDefinition(): FloraDefinition | null {
    if (!this.activeDefinitionId) return null;
    return this.deps.catalog.find(this.activeDefinitionId) ?? null;
  }

  /** Take the mode and the brush size from the plant that has just been armed. */
  private armForDefinition(): void {
    const def = this.currentDefinition();
    if (!def) return;
    this.mode = defaultPlantMode(def);
    this.brush.radiusMeters = suggestedBrushRadius(def);
  }

  private pointerToNdc(e: PointerEvent): THREE.Vector2 {
    const rect = this.deps.domElement.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
    const y = -((e.clientY - rect.top) / rect.height) * 2 + 1;
    return new THREE.Vector2(x, y);
  }

  /**
   * Terrain height at a **world** point.
   *
   * `Terrain.sampleHeight` is indexed in terrain-local metres, and the site's mesh is rotated by its
   * north orientation, so every world point has to be brought back through {@link Terrain.toLocal}
   * first. Everything the tool handles — raycast hits, plant positions, site objects — is in world
   * space, so the conversion belongs here rather than at each of the four call sites.
   */
  private sampler(terrain: Terrain): HeightSampler {
    return (x, z) => {
      terrain.toLocal(x, z, this.local);
      return terrain.sampleHeight(this.local.x, this.local.y);
    };
  }

  private surfaceAt(terrain: Terrain): (x: number, z: number) => SurfaceLayerId {
    return (x, z) => {
      terrain.toLocal(x, z, this.local);
      return terrain.sampleSurface(this.local.x, this.local.y);
    };
  }

  /**
   * The ground rules a placement is judged against, gathered once.
   *
   * Built per hover and per *dab* rather than per plant: a dab throws up to sixty-four darts, and
   * `siteObjects()` copies the site's buildings into a fresh array every time it is asked. Sixty-four
   * copies of the same list per dab, several dabs a frame, is the difference between a brush that
   * keeps up with the mouse on a built site and one that does not.
   */
  private placementRules(terrain: Terrain): PlacementContext {
    return {
      surfaceAt: this.surfaceAt(terrain),
      ...(this.deps.siteObjects ? { siteObjects: this.deps.siteObjects() } : {}),
    };
  }

  private handlePointerMove(e: PointerEvent): void {
    if (!this.active) return;
    const terrain = this.deps.terrain();
    const def = this.currentDefinition();
    if (!terrain || !def) return;

    const ndc = this.pointerToNdc(e);
    const hit = this.raycaster.intersect(ndc, this.deps.getCamera(), terrain.mesh);
    if (!hit) {
      // Mid-stroke the pointer is allowed to wander off the mesh — over the horizon, or into a pit
      // the ray no longer meets — without the brush stuttering. It simply keeps the last point.
      if (this.pressed) return;
      this.ghostRoot.visible = false;
      this.brushCursor.setVisible(false);
      this.lastPoint = null;
      return;
    }

    const position: Vec3 = { x: hit.x, y: hit.y, z: hit.z };
    const validity = evaluatePlacement(
      def,
      position,
      this.sampler(terrain),
      this.placementRules(terrain),
    );
    this.lastPoint = hit;
    this.lastValidity = validity;

    this.ghostRoot.position.set(position.x, position.y, position.z);
    this.ghostRoot.visible = true;
    this.applyValidityColor(validity.valid);
    this.refreshBrushCursor();
    this.updateReadout();
  }

  private handlePointerDown(e: PointerEvent): void {
    if (!this.active || e.button !== 0) return;
    // Pressing the mouse is something happening between press and release of Ctrl, so whatever this
    // click does, the modifier goes back to being borrowed rather than kept.
    this.tapPending = false;
    const terrain = this.deps.terrain();
    const def = this.currentDefinition();
    if (!terrain || !def || !this.lastPoint) return;

    if (this.effectiveMode === 'paint') {
      this.beginStroke(e, def);
      return;
    }

    const instance = createFloraInstance(def.id, {
      position: { x: this.lastPoint.x, y: this.lastPoint.y, z: this.lastPoint.z },
      maturity: this.previewMaturity,
      visualSeed: this.nextVisualSeed,
    });
    this.nextVisualSeed += 1;
    this.deps.onPlace(instance);
    // Vary the next ghost so repeated placement looks natural.
    this.rebuildGhost();
    this.applyMode();
  }

  private handlePointerUp(e: PointerEvent): void {
    if (!this.pressed || (this.pointerId !== null && e.pointerId !== this.pointerId)) return;
    if (this.pointerId !== null && this.deps.domElement.hasPointerCapture(this.pointerId)) {
      this.deps.domElement.releasePointerCapture(this.pointerId);
    }
    this.pointerId = null;
    this.commitStroke();
  }

  private handleKeyDown(e: KeyboardEvent): void {
    if (!this.active) return;
    if (e.key === 'Escape') {
      this.cancel();
      return;
    }
    if (isModeKey(e.key)) {
      // `repeat` fires while the key is held down; only the first press starts a possible tap.
      if (e.repeat || this.borrowing) return;
      this.borrowing = true;
      this.tapPending = true;
      this.applyMode();
      this.announceMode();
      this.deps.onChange?.();
      return;
    }
    // Any other key pressed while Ctrl is down makes this a chord, not a tap.
    this.tapPending = false;
  }

  private handleKeyUp(e: KeyboardEvent): void {
    if (!this.active || !isModeKey(e.key)) return;
    this.releaseBorrow(this.tapPending);
  }

  /**
   * Let go of the borrowed mode. `keep` turns the hold into a tap — the borrowed mode becomes the
   * one that stays, which is the same flip the toolbar and {@link togglePlantMode} perform.
   */
  private releaseBorrow(keep: boolean): void {
    if (!this.borrowing && !this.tapPending) return;
    const borrowed = otherPlantMode(this.mode);
    this.borrowing = false;
    this.tapPending = false;
    if (keep) this.mode = borrowed;
    this.applyMode();
    this.updateReadout();
    this.announceMode();
    this.deps.onChange?.();
  }

  private handleWheel(e: WheelEvent): void {
    if (!this.active || this.effectiveMode !== 'paint') return;
    const radius = e.ctrlKey || e.metaKey;
    const density = e.shiftKey && !radius;
    if (!radius && !density) return;
    // Claim it before the camera's listener runs, or the site zooms while the brush resizes.
    e.preventDefault();
    e.stopPropagation();
    if (radius) {
      // This hold is now sizing the brush, so it stops flipping the mode — otherwise the pointer
      // would draw the spot glyph for as long as the user kept Ctrl down to keep sizing. It stays
      // stopped until Ctrl is released and pressed again: only a fresh, non-repeat keydown can
      // start another borrow.
      this.tapPending = false;
      if (this.borrowing) {
        this.borrowing = false;
        this.applyMode();
      }
      // Multiplicative, so one notch is the same *proportional* change at 30 cm and at 10 m.
      this.setBrushRadius(this.brush.radiusMeters * Math.exp(-e.deltaY * 0.0015));
    } else {
      this.setBrushDensity(this.brush.density - e.deltaY * 0.0012);
    }
    this.announceBrush();
  }

  /* ---------------------------------- Strokes ---------------------------------- */

  private beginStroke(e: PointerEvent, def: FloraDefinition): void {
    this.deps.domElement.setPointerCapture(e.pointerId);
    this.pointerId = e.pointerId;
    this.pressed = true;
    // Locked at press, so letting go of Ctrl or spinning the density wheel halfway through a drag
    // cannot leave one gesture laid out at two different spacings.
    this.strokeSpacing = scatterSpacingMeters(def, this.brush.density);
    this.strokePlanted = [];
    this.carryMeters = 0;
    this.holdSeconds = 0;

    this.strokeField = new ScatterField(this.strokeSpacing);
    // Seeded with what is already growing here, so a second pass over a bed thickens its gaps
    // instead of doubling up on every plant in it. Same species only — see `PlantBrush`.
    for (const instance of this.deps.world.all()) {
      if (instance.floraDefinitionId === def.id) {
        this.strokeField.seed(instance.position.x, instance.position.z);
      }
    }

    const start = this.lastPoint;
    if (start) {
      // A click with no drag still has to plant something, so the first dab lands immediately.
      this.applyDab({ x: start.x, z: start.z }, def);
      this.lastDabPoint = { x: start.x, z: start.z };
    }
    e.preventDefault();
  }

  /**
   * Advance the active stroke. Driven by the frame loop rather than by pointer events, so a held
   * brush keeps filling and a fast drag is resampled at a fixed spacing in metres — the same
   * guarantee the ground tools give, and for the same reason (`TerrainBrush`).
   */
  tick(deltaSeconds: number): void {
    if (!this.pressed || !this.lastPoint) return;
    const def = this.currentDefinition();
    if (!def) return;

    const here: BrushPoint = { x: this.lastPoint.x, z: this.lastPoint.z };
    const spacing = Math.max(0.05, PLANT_DAB_SPACING * this.brush.radiusMeters);
    let emitted = 0;

    if (this.lastDabPoint) {
      const sampling = sampleStrokeSegment(this.lastDabPoint, here, spacing, this.carryMeters);
      for (const dab of sampling.dabs) this.applyDab(dab, def);
      emitted = sampling.dabs.length;
      this.carryMeters = sampling.carryMeters;
      this.lastDabPoint = here;
    }

    if (emitted === 0) {
      // Not moving, or not far enough to owe a dab: pay out on the clock instead. The spacing test
      // saturates a stationary disc within a second or so, so this fills a spot and then stops.
      this.holdSeconds += Math.max(0, deltaSeconds);
      const hold = holdDabCount(this.holdSeconds, PLANT_HOLD_DABS_PER_SECOND, 3);
      this.holdSeconds = hold.remainderSeconds;
      for (let i = 0; i < hold.count; i += 1) this.applyDab(here, def);
    } else {
      this.holdSeconds = 0;
    }
  }

  /** Throw one dab's worth of darts at the ground and plant whatever survives both tests. */
  private applyDab(center: BrushPoint, def: FloraDefinition): void {
    const terrain = this.deps.terrain();
    const field = this.strokeField;
    if (!terrain || !field || this.strokePlanted.length >= MAX_PLANTS_PER_STROKE) return;

    const sampleHeight = this.sampler(terrain);
    const rules = this.placementRules(terrain);
    const darts = scatterDarts(
      center,
      this.brush.radiusMeters,
      dartsPerDab(this.brush.radiusMeters, this.strokeSpacing),
    );
    for (const dart of darts) {
      if (this.strokePlanted.length >= MAX_PLANTS_PER_STROKE) return;
      // Spacing first: it is a squared distance against nine buckets, where the placement test
      // costs four height samples and a walk over the site's buildings.
      if (!field.isClear(dart.x, dart.z)) continue;
      const position: Vec3 = { x: dart.x, y: sampleHeight(dart.x, dart.z), z: dart.z };
      if (!evaluatePlacement(def, position, sampleHeight, rules).valid) continue;
      field.accept(dart.x, dart.z);

      const variation = scatterVariation(this.previewMaturity);
      const instance = createFloraInstance(def.id, {
        position,
        rotation: { x: 0, y: variation.rotationY, z: 0 },
        maturity: variation.maturity,
        visualSeed: this.nextVisualSeed,
      });
      this.nextVisualSeed += 1;
      this.strokePlanted.push(instance);
      this.deps.onPaintPlace?.(instance);
    }
    this.updateReadout();
  }

  /**
   * End the stroke and record it as one step. A stroke that planted nothing — a drag across a
   * driveway, or a second pass over ground that is already full — records nothing at all rather
   * than leaving an empty entry in the history.
   */
  private commitStroke(): void {
    if (!this.pressed) return;
    this.pressed = false;
    this.pointerId = null;
    this.lastDabPoint = null;
    this.carryMeters = 0;
    this.holdSeconds = 0;
    this.strokeField = null;

    const planted = this.strokePlanted;
    this.strokePlanted = [];
    if (planted.length === 0) return;
    this.deps.onPaintCommit?.(planted);

    const def = this.currentDefinition();
    const name = def ? plantName(def) : 'plants';
    this.deps.onNotice?.(
      `${planted.length} ${name.toLowerCase()}${planted.length === 1 ? '' : ' planted'}`,
    );
    this.updateReadout();
  }

  /* ---------------------------------- Feedback ---------------------------------- */

  /**
   * Show the parts of the preview the current mode actually uses.
   *
   * In paint mode the translucent specimen is dropped and the brush ring takes its place: a single
   * ghost standing in the middle of the area about to be filled says the wrong thing about what the
   * next press will do, and it hides the ground you are aiming at. The footprint ring stays in both,
   * because "how big is one of these" is worth knowing while you decide how wide to make the brush.
   */
  private applyMode(): void {
    const painting = this.effectiveMode === 'paint';
    if (this.ghostBody) this.ghostBody.visible = !painting;
    if (this.canopyRing) this.canopyRing.visible = !painting;
    this.refreshBrushCursor();
  }

  private refreshBrushCursor(): void {
    const terrain = this.deps.terrain();
    if (!this.active || this.effectiveMode !== 'paint' || !terrain || !this.lastPoint) {
      this.brushCursor.setVisible(false);
      return;
    }
    terrain.toLocal(this.lastPoint.x, this.lastPoint.z, this.local);
    // A hardness of 1: the brush has no falloff — a plant is either scattered or it is not — so the
    // inner ring sits on the outer one and the cursor reads as the single hard edge it really is.
    this.brushCursor.update(terrain, this.local.x, this.local.y, this.brush.radiusMeters, 1);
    this.brushCursor.setVisible(true);
  }

  /** Say which mode is armed, at the pointer, where the user is looking. */
  private announceMode(): void {
    const def = this.currentDefinition();
    if (!def) return;
    const mode = this.effectiveMode;
    const suffix = mode === 'paint' ? ' — drag to scatter' : ' — click to place';
    this.deps.onNotice?.(`${plantModeLabel(mode)}: ${plantName(def)}${suffix}`);
  }

  private announceBrush(): void {
    this.deps.onNotice?.(
      `Brush ${this.brush.radiusMeters.toFixed(1)} m · ${this.spacingMeters.toFixed(2)} m apart`,
    );
  }

  private rebuildGhost(): void {
    const def = this.currentDefinition();
    this.disposeGhost();
    if (!def) return;

    const instance = createFloraInstance(def.id, {
      maturity: this.previewMaturity,
      visualSeed: this.nextVisualSeed,
    });
    instance.position = { x: 0, y: 0, z: 0 };
    const body = this.factory.build(def, instance);
    body.position.set(0, 0, 0);
    this.makeTranslucent(body);
    this.ghostBody = body;
    this.ghostRoot.add(body);

    this.footprintRing = this.makeRing(
      placementFootprintRadius(def, this.previewMaturity),
      0x4caf50,
    );
    this.canopyRing = this.makeRing(matureFootprintRadius(def), 0x2196f3, 0.4);
    this.ghostRoot.add(this.footprintRing, this.canopyRing);
  }

  private makeRing(radius: number, color: number, opacity = 0.8): THREE.Line {
    const segments = 48;
    const points: THREE.Vector3[] = [];
    for (let i = 0; i <= segments; i += 1) {
      const a = (i / segments) * Math.PI * 2;
      points.push(new THREE.Vector3(Math.cos(a) * radius, 0.02, Math.sin(a) * radius));
    }
    const geometry = new THREE.BufferGeometry().setFromPoints(points);
    const material = new THREE.LineBasicMaterial({ color, transparent: true, opacity });
    const line = new THREE.Line(geometry, material);
    line.name = 'placement-ring';
    return line;
  }

  private makeTranslucent(object: THREE.Object3D): void {
    object.traverse((obj) => {
      const mesh = obj as Partial<THREE.Mesh>;
      const material = mesh.material;
      const apply = (m: THREE.Material): void => {
        m.transparent = true;
        m.opacity = 0.55;
        m.depthWrite = false;
      };
      if (Array.isArray(material)) material.forEach(apply);
      else if (material) apply(material);
    });
  }

  private applyValidityColor(valid: boolean): void {
    const color = valid ? 0x4caf50 : 0xe53935;
    // The brush ring wears it too: in paint mode the footprint ring is a hairline inside a much
    // larger circle, and the thing the eye is actually following has to be the thing that reddens.
    this.brushCursor.setColor(color);
    if (!this.footprintRing) return;
    const material = this.footprintRing.material as THREE.LineBasicMaterial;
    material.color.setHex(color);
  }

  private disposeGhost(): void {
    if (this.ghostBody) {
      this.factory.dispose(this.ghostBody);
      this.ghostRoot.remove(this.ghostBody);
      this.ghostBody = null;
    }
    for (const ring of [this.footprintRing, this.canopyRing]) {
      if (!ring) continue;
      ring.geometry.dispose();
      (ring.material as THREE.Material).dispose();
      this.ghostRoot.remove(ring);
    }
    this.footprintRing = null;
    this.canopyRing = null;
  }

  private updateReadout(): void {
    if (!this.deps.readout) return;
    const def = this.currentDefinition();
    if (!def) {
      this.clearReadout();
      return;
    }
    const name = plantName(def);
    const slope = this.lastValidity ? ` · slope ${this.lastValidity.slopeDegrees.toFixed(0)}°` : '';
    const status = describeRejection(this.lastValidity);
    this.deps.readout.textContent = `${this.describeMode(def, name)}${slope}${status}`;
  }

  /**
   * The plant, described by whichever of its numbers the armed mode is about.
   *
   * Spot planting is about one specimen, so it reports that specimen's height and spread. Painting
   * is about a bed, so it reports the two figures that decide what the bed looks like — how wide
   * the brush is and how far apart the plants land — plus a running count while a stroke is down,
   * which is the only place the cost of a drag is visible before it is committed.
   */
  private describeMode(def: FloraDefinition, name: string): string {
    if (this.effectiveMode !== 'paint') {
      const state = computeGrowthState(def.growth, this.previewMaturity);
      return (
        `${name} — ${state.heightMeters.toFixed(1)} m tall, ` +
        `${state.spreadMeters.toFixed(1)} m wide`
      );
    }
    const running = this.pressed ? ` · ${this.strokePlanted.length} planted` : '';
    return (
      `${name} — brush ${this.brush.radiusMeters.toFixed(1)} m · ` +
      `${this.spacingMeters.toFixed(2)} m apart${running}`
    );
  }

  private clearReadout(): void {
    if (this.deps.readout) this.deps.readout.textContent = '';
  }
}

/** The key that borrows the other mode: Ctrl, or Cmd where that is the platform's modifier. */
function isModeKey(key: string): boolean {
  return key === 'Control' || key === 'Meta';
}

/** What the app calls a plant when it has one line to do it in. */
function plantName(def: FloraDefinition): string {
  return def.commonNames[0] ?? def.botanicalName;
}
